import { format } from 'date-fns';
import { AlertTriangle, ChevronLeft, ChevronRight, Eye, Terminal } from 'lucide-react';
import React, { useState } from 'react';
import type { DateRange } from 'react-day-picker';
import Content from '~/components/layout/Content';
import { Badge } from '~/components/ui/badge'; // Adjust path
import { Button } from '~/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from '~/components/ui/dialog'; // Adjust path
import { ScrollArea } from '~/components/ui/scroll-area'; // Adjust path
import { Separator } from '~/components/ui/separator'; // Adjust path

import { CalendarIcon, Search } from 'lucide-react';
import { trpc } from '~/api/trpc';
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card';
import { Skeleton } from '~/components/ui/skeleton'; // Adjust path
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '~/components/ui/table'; // Adjust path
import { useDebounce } from '~/hooks/use-debounce';

import { keepPreviousData } from '@tanstack/react-query';
import { z } from 'zod';
import { Calendar } from '~/components/ui/calendar'; // Adjust path
import { Input } from '~/components/ui/input'; // Adjust path
import { Label } from '~/components/ui/label';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '~/components/ui/popover'; // Adjust path
import { Switch } from '~/components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '~/components/ui/select'; // Adjust path
import { cn } from '~/lib/utils'; // Your shadcn utils path
import { LogEntry, LogEntryData, LogObjectType, LogObjectTypesEnum } from '~/types/server-types/LogTypes';
import { useUserStore } from '~/stores/user-store';
import { useShallow } from 'zustand/react/shallow';
import dayjs from 'dayjs';
import LocalDateTime from '~/lib/localdatetime';

interface LogFiltersProps {
    dateRange?: DateRange;
    onDateRangeChange: (range: DateRange | undefined) => void;
    objectType?: LogObjectType;
    onObjectTypeChange: (type: LogObjectType | undefined) => void;
    searchQuery: string;
    onSearchQueryChange: (query: string) => void;
    onlyWithErrors: boolean;
    onOnlyWithErrorsChange: (onlyWithErrors: boolean) => void;
    className?: string;
}

// Helper to capitalize enum values for display
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export const LogFilters: React.FC<LogFiltersProps> = ({
    dateRange,
    onDateRangeChange,
    objectType,
    onObjectTypeChange,
    searchQuery,
    onSearchQueryChange,
    onlyWithErrors,
    onOnlyWithErrorsChange,
    className,
}) => {
    return (
        <div className={cn('flex flex-wrap items-center gap-4', className)}>
            {/* Date Range Picker */}
            <Popover>
                <PopoverTrigger asChild>
                    <Button
                        id="date"
                        variant={'outline'}
                        className={cn(
                            'w-[260px] justify-start text-left font-normal',
                            !dateRange && 'text-muted-foreground'
                        )}
                    >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateRange?.from ? (
                            dateRange.to ? (
                                <>
                                    {format(dateRange.from, 'LLL dd, y')} -{' '}
                                    {format(dateRange.to, 'LLL dd, y')}
                                </>
                            ) : (
                                format(dateRange.from, 'LLL dd, y')
                            )
                        ) : (
                            <span>Pick a date range</span>
                        )}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                        mode="range"
                        defaultMonth={dateRange?.from}
                        selected={dateRange}
                        onSelect={onDateRangeChange}
                        numberOfMonths={2}
                    />
                </PopoverContent>
            </Popover>

            {/* Object Type Select */}
            <Select
                value={objectType}
                onValueChange={(value) => onObjectTypeChange(value ? value as LogObjectType : undefined)}
            >
                <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Object Type" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">All Types</SelectItem> {/* Add option to clear filter */}
                    {Object.values(LogObjectTypesEnum).map((type) => (
                        <SelectItem key={type} value={type}>
                            {capitalize(type)}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>

            {/* Search Input */}
            <div className="relative flex-grow min-w-[200px]">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                    type="search"
                    placeholder="Search user or action..."
                    value={searchQuery}
                    onChange={(e) => onSearchQueryChange(e.target.value)}
                    className="pl-8 w-full"
                />
            </div>

            {/* Errors Only Toggle */}
            <div className="flex items-center gap-2">
                <Switch
                    id="onlyWithErrors"
                    checked={onlyWithErrors}
                    onCheckedChange={onOnlyWithErrorsChange}
                />
                <Label htmlFor="onlyWithErrors" className="whitespace-nowrap">Only entries with errors</Label>
            </div>
        </div>
    );
};

interface LogsTableProps {
    logs: LogEntry[];
    isLoading: boolean;
    itemsPerPage: number; // Needed for skeleton rows
}

// Helper to get a badge variant based on object type
const getObjectTypeLabels = (log: LogEntry): string[] => {
    const labels: string[] = [];

    // Check each relevant foreign key independently
    if (log.appointmentId) labels.push('Appointment');
    if (log.appointmentStepId) labels.push('Appt Step'); // Maybe shorten labels if needed
    if (log.treatmentId) labels.push('Treatment');
    if (log.treatmentVariantId) labels.push('Variant');
    if (log.treatmentStepId) labels.push('Treat Step');
    if (log.customerId) labels.push('Customer');
    if (log.practitionerId) labels.push('Practitioner');
    if (log.practitionerCategoryId) labels.push('Prac Cat'); // Example abbreviation
    if (log.roomId) labels.push('Room');
    if (log.roomCategoryId) labels.push('Room Cat');
    if (log.equipmentId) labels.push('Equipment');
    if (log.equipmentCategoryId) labels.push('Equip Cat');
    if (log.paymentId) labels.push('Payment');
    if (log.billingDraftId) labels.push('Auftrag');

    return labels;
}

export const LogsTable: React.FC<LogsTableProps> = ({ logs, isLoading, itemsPerPage }) => {
    const [selectedLog, setSelectedLog] = useState<LogEntry | null>(null);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);

    const handleViewDetails = (log: LogEntry) => {
        setSelectedLog(log);
        setIsDetailsOpen(true);
    };

    const renderSkeleton = () => (
        Array.from({ length: itemsPerPage }).map((_, index) => (
            <TableRow key={`skeleton-${index}`}>
                <TableCell><Skeleton className="h-4 w-3/4" /></TableCell>
                <TableCell><Skeleton className="h-4 w-1/2" /></TableCell>
                <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                <TableCell><Skeleton className="h-4 w-4 mx-auto" /></TableCell>
                <TableCell><Skeleton className="h-4 w-16" /> <Skeleton className="h-4 w-12" /></TableCell>
                <TableCell><Skeleton className="h-8 w-8 rounded-md" /></TableCell>
            </TableRow>
        ))
    );

    return (
        <>
            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-[180px]">Timestamp</TableHead>
                            <TableHead className="w-[150px]">User</TableHead>
                            <TableHead>Action</TableHead>
                            <TableHead className="w-[70px] text-center">Errors</TableHead>
                            <TableHead className="w-[120px]">Objects</TableHead>
                            <TableHead className="w-[50px]">View</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            renderSkeleton()
                        ) : logs.length > 0 ? (
                            logs.map((log) => {
                                // Get all applicable labels
                                const objectLabels = getObjectTypeLabels(log);
                                return (
                                    <TableRow key={log.id}>
                                        <TableCell>{format(new Date(log.createdAt), 'yyyy-MM-dd HH:mm')}</TableCell> {/* Slightly shorter format? */}
                                        <TableCell>{log.userName ?? 'System'}</TableCell>
                                        <TableCell className="max-w-xs truncate"> {/* Removed truncate, let wrap? */}
                                            {/* Optional: Add tooltip for full text? */}
                                            {log.actionExplanation}
                                        </TableCell>
                                        <TableCell className="text-center">
                                            {log.errors && log.errors.length > 0 ? (
                                                // Icon only — the messages themselves are in the details dialog
                                                <span
                                                    className="inline-flex items-center gap-1 text-destructive"
                                                    title={log.errors.join('\n')}
                                                >
                                                    <AlertTriangle className="h-4 w-4" />
                                                    {log.errors.length > 1 && (
                                                        <span className="text-xs">{log.errors.length}</span>
                                                    )}
                                                </span>
                                            ) : (
                                                <span className="text-xs text-muted-foreground">—</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {/* Render badges if any labels exist */}
                                            {objectLabels.length > 0 ? (
                                                <div className="flex flex-wrap gap-1"> {/* Wrap badges */}
                                                    {objectLabels.map((label) => (
                                                        <Badge key={label} variant="secondary" className="whitespace-nowrap">
                                                            {label}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="text-xs text-muted-foreground">N/A</span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => handleViewDetails(log)}
                                                aria-label="View Details"
                                            >
                                                <Eye className="h-4 w-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            // ... No logs found row remains the same ...
                            <TableRow>
                                <TableCell colSpan={6} className="h-24 text-center">
                                    No logs found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Details Dialog */}
            {selectedLog && (
                <LogDetailsDialog
                    log={selectedLog}
                    isOpen={isDetailsOpen}
                    onOpenChange={setIsDetailsOpen}
                />
            )}
        </>
    );
};

interface LogDetailsDialogProps {
    log: LogEntry | null;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
}

export const LogDetailsDialog: React.FC<LogDetailsDialogProps> = ({
    log,
    isOpen,
    onOpenChange,
}) => {
    const { timezone } = useUserStore(
        useShallow((state) => ({
            timezone: state.user?.timezone || 'Europe/Berlin',
        }))
    );

    if (!log) return null;

    // Helper function to render related object details
    const renderRelatedObject = (label: string, data: LogEntryData | null | undefined) => {
        if (!data) return null;
        return (
            <div className="mt-2">
                <p className="text-sm font-medium text-muted-foreground">{label}:</p>
                <p className="text-sm">ID: {data.id}</p>
                {'name' in data && <p className="text-sm">Name: {data.name}</p>}
                {('firstName' in data && 'lastName' in data) && <p className="text-sm">Name: {`${data.firstName} ${data.lastName}`}</p>}
                {'email' in data && <p className="text-sm">Email: {data.email}</p>}
                {'steps' in data && (
                    <p className="text-sm">
                        Appointment Time:{' '}
                        {LocalDateTime.deserialize(data.steps.sort((a, b) => new Date(a.dateFrom).getTime() - new Date(b.dateFrom).getTime())[0].dateFromLocal!).format('yyyy.MM.dd HH:mm')}{' - '}
                        {LocalDateTime.deserialize(data.steps.sort((a, b) => new Date(b.dateUntil).getTime() - new Date(a.dateUntil).getTime())[0].dateUntilLocal!).format('yyyy.MM.dd HH:mm')}
                    </p>
                )}
                {'originalTreatmentVariants' in data && <p className="text-sm">Services: {data.originalTreatmentVariants.map((assoc) => assoc.treatmentVariant.name).join(", ")}</p>}
            </div>
        );
    }

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle>Log Details</DialogTitle>
                    <DialogDescription>
                        Detailed information for log entry ID: {log.id}
                    </DialogDescription>
                </DialogHeader>
                <ScrollArea className="max-h-[60vh] pr-6"> {/* Add ScrollArea for potentially long content */}
                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                            <span className="text-right text-sm font-medium text-muted-foreground">Timestamp:</span>
                            <span className="col-span-3 text-sm">{format(new Date(log.createdAt), 'yyyy-MM-dd HH:mm')}</span>
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                            <span className="text-right text-sm font-medium text-muted-foreground">User:</span>
                            <span className="col-span-3 text-sm">{log.userName ?? <Badge variant="outline">System</Badge>}</span>
                        </div>
                        <div className="grid grid-cols-4 items-start gap-4">
                            <span className="text-right text-sm font-medium text-muted-foreground pt-1">Action:</span>
                            <p className="col-span-3 text-sm">{log.actionExplanation}</p>
                        </div>
                        {log.errors && log.errors.length > 0 && (
                            <div className="grid grid-cols-4 items-start gap-4">
                                <span className="text-right text-sm font-medium text-muted-foreground pt-1">Errors:</span>
                                <ul className="col-span-3 space-y-1">
                                    {log.errors.map((error, index) => (
                                        <li key={index} className="text-sm text-destructive">{error}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <Separator className="my-2" />

                        <h4 className="text-md font-semibold mt-2 mb-1">Related Objects</h4>
                        {renderRelatedObject("Customer", log.customer)}
                        {renderRelatedObject("Appointment", log.appointment)}
                        {renderRelatedObject("Appointment Step", log.appointmentStep)}
                        {renderRelatedObject("Treatment", log.treatment)}
                        {renderRelatedObject("Treatment Variant", log.treatmentVariant)}
                        {renderRelatedObject("Treatment Step", log.treatmentStep)}
                        {renderRelatedObject("Practitioner", log.practitioner)}
                        {renderRelatedObject("Practitioner Category", log.practitionerCategory)}
                        {renderRelatedObject("Room", log.room)}
                        {renderRelatedObject("Room Category", log.roomCategory)}
                        {renderRelatedObject("Equipment", log.equipment)}
                        {renderRelatedObject("Equipment Category", log.equipmentCategory)}
                        {log.payment && (
                            <div className="mt-2">
                                <p className="text-sm font-medium text-muted-foreground">Payment:</p>
                                <p className="text-sm">
                                    ID: <a href={`/payment/${log.payment.id}`} className="text-primary hover:underline">{log.payment.id}</a>
                                </p>
                                <p className="text-sm">
                                    Betrag: {(log.payment.paidAmountCents / 100).toFixed(2)} EUR
                                </p>
                                {log.payment.billingDraft && (
                                    <p className="text-sm">Auftrag: {log.payment.billingDraft.billingDraftNumber}</p>
                                )}
                            </div>
                        )}
                        {log.billingDraft && (
                            <div className="mt-2">
                                <p className="text-sm font-medium text-muted-foreground">Auftrag:</p>
                                <p className="text-sm">
                                    <a href={`/billing-draft/${log.billingDraft.id}`} className="text-primary hover:underline">
                                        {log.billingDraft.billingDraftNumber}
                                    </a>
                                </p>
                            </div>
                        )}

                        {/* Optionally display raw foreign keys if no relation data exists */}
                        {/* ... */}

                    </div>
                </ScrollArea>
                {/* Optional Footer
         <DialogFooter>
           <Button onClick={() => onOpenChange(false)}>Close</Button>
         </DialogFooter>
         */}
            </DialogContent>
        </Dialog>
    );
};

interface PaginationControlsProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    canPreviousPage: boolean;
    canNextPage: boolean;
}

const PaginationControls: React.FC<PaginationControlsProps> = ({
    currentPage,
    totalPages,
    onPageChange,
    canPreviousPage,
    canNextPage,
}) => {
    return (
        <div className="flex items-center justify-end space-x-2 py-4">
            <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
            </span>
            <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={!canPreviousPage}
            >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
            </Button>
            <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={!canNextPage}
            >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
        </div>
    );
};


const ITEMS_PER_PAGE = 50;

const LogsPage = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
    const [objectType, setObjectType] = useState<LogObjectType | undefined>(undefined);
    const [searchQuery, setSearchQuery] = useState('');
    const [onlyWithErrors, setOnlyWithErrors] = useState(false);
    const debouncedSearchQuery = useDebounce(searchQuery, 300);

    const logsQuery = trpc.logs.getLogs.useQuery(
        {
            page: currentPage,
            pageSize: ITEMS_PER_PAGE,
            startDate: dateRange?.from?.toISOString(),
            endDate: dateRange?.to?.toISOString(),
            objectType: objectType,
            searchQuery: debouncedSearchQuery || undefined,
            onlyWithErrors: onlyWithErrors || undefined,
        },
        {
            placeholderData: keepPreviousData,
        }
    );

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= (logsQuery.data?.totalPages ?? 1)) {
            setCurrentPage(newPage);
        }
    };

    const handleObjectTypeChange = (type: LogObjectType | undefined | "all") => {
        // Reset page to 1 when filters change
        setCurrentPage(1);
        setObjectType(type === "all" ? undefined : type as LogObjectType);
    };

    const handleDateRangeChange = (range: DateRange | undefined) => {
        // Reset page to 1 when filters change
        setCurrentPage(1);
        setDateRange(range);
    }

    const handleOnlyWithErrorsChange = (value: boolean) => {
        // Reset page to 1 when filters change
        setCurrentPage(1);
        setOnlyWithErrors(value);
    }

    const handleSearchChange = (query: string) => {
        // Reset page to 1 when filters change *only* when starting/clearing search
        // This avoids resetting the page on every keystroke due to debounce
        if ((!searchQuery && query) || (searchQuery && !query)) {
            setCurrentPage(1);
        }
        setSearchQuery(query);
    }


    return (
        <Content>
            <div className="container mx-auto py-10">
                <h1 className="text-3xl font-bold mb-6">System Logs</h1>

                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle>Filter Logs</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <LogFilters
                            dateRange={dateRange}
                            onDateRangeChange={handleDateRangeChange}
                            objectType={objectType}
                            onObjectTypeChange={handleObjectTypeChange}
                            searchQuery={searchQuery}
                            onSearchQueryChange={handleSearchChange}
                            onlyWithErrors={onlyWithErrors}
                            onOnlyWithErrorsChange={handleOnlyWithErrorsChange}
                        />
                    </CardContent>
                </Card>

                {logsQuery.isError && (
                    <Alert variant="destructive" className="mb-6">
                        <Terminal className="h-4 w-4" />
                        <AlertTitle>Error Fetching Logs</AlertTitle>
                        <AlertDescription>
                            {logsQuery.error?.message ?? "An unknown error occurred."}
                        </AlertDescription>
                    </Alert>
                )}

                <Card className='p-0 border-none'>
                    <CardContent className="p-0 m-0"> {/* Remove padding if table has its own */}
                        <LogsTable
                            logs={logsQuery.data?.logs ?? []}
                            isLoading={logsQuery.isLoading}
                            itemsPerPage={ITEMS_PER_PAGE}
                        />
                    </CardContent>
                </Card>

                {logsQuery.data && logsQuery.data.totalPages > 1 && (
                    <PaginationControls
                        currentPage={logsQuery.data.currentPage}
                        totalPages={logsQuery.data.totalPages}
                        onPageChange={handlePageChange}
                        canPreviousPage={logsQuery.data.currentPage > 1}
                        canNextPage={logsQuery.data.currentPage < logsQuery.data.totalPages}
                    />
                )}
            </div>
        </Content>
    );
};

export default LogsPage;